// ─── Clinic hours — the single place to change opening / closing / lunch times ──
// Times are 24h "HH:MM" in IST. Set lunchStart/lunchEnd to null for no lunch break.
// Usual hours are informational only (a light "after hours" / "lunch" label);
// every future slot in CALENDAR_RANGE stays bookable — treatments can run late.
export const CLINIC_HOURS = {
  open:        '10:00',
  close:       '19:00',
  lunchStart:  '13:00',
  lunchEnd:    '14:00',
  slotMinutes: 15,       // granularity of the calendar grid, time dropdown, timeline and walk-ins
}

// Range shown on the calendar grid and offered in the time dropdown
export const CALENDAR_RANGE = { start: '08:00', end: '22:00' }

export function toMins(t) { const [h, m] = String(t || '00:00').split(':').map(Number); return h * 60 + (m || 0) }
export function toTime(mins) { return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}` }

// ─── IST clock (independent of the device's timezone) ───
export function nowIST() {
  const d = new Date()
  return new Date(d.getTime() + (d.getTimezoneOffset() + 330) * 60000)
}
export function todayIST() {
  const d = nowIST()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
export function nowMinsIST() { const d = nowIST(); return d.getHours() * 60 + d.getMinutes() }

export function isLunch(mins) {
  const { lunchStart, lunchEnd } = CLINIC_HOURS
  return !!(lunchStart && lunchEnd) && mins >= toMins(lunchStart) && mins < toMins(lunchEnd)
}

// Every slot start in the calendar range (8:00 AM … 9:45 PM) — lunch/after hours included
export function daySlots() {
  const step = CLINIC_HOURS.slotMinutes
  const out = []
  for (let m = toMins(CALENDAR_RANGE.start); m < toMins(CALENDAR_RANGE.end); m += step) out.push(toTime(m))
  return out
}

// Light, non-blocking label for slots outside usual hours
export function slotLabel(time) {
  const m = toMins(time)
  if (isLunch(m)) return 'lunch'
  if (m < toMins(CLINIC_HOURS.open) || m >= toMins(CLINIC_HOURS.close)) return 'after hours'
  return ''
}

export function isWithinHours(time) {
  const m = toMins(time)
  return m >= toMins(CLINIC_HOURS.open) && m < toMins(CLINIC_HOURS.close) && !isLunch(m)
}

// Start of the slot we are currently in (e.g. 16:07 → 16:00). Walk-ins use this.
export function currentSlotMins() {
  const step = CLINIC_HOURS.slotMinutes
  return Math.floor(nowMinsIST() / step) * step
}
export function walkInTime() { return toTime(currentSlotMins()) }

// A slot is "past" once its whole slot window is over (the current slot still counts as now)
export function isPastSlot(date, time) {
  const today = todayIST()
  if (!date) return false
  if (date < today) return true
  if (date > today) return false
  return toMins(time) < currentSlotMins()
}

export function bookableSlots(date, { allowPast = false } = {}) {
  return daySlots().filter(t => allowPast || !isPastSlot(date, t))
}

export function nextAvailableSlot(date) {
  return bookableSlots(date)[0] || null
}

export function tomorrowISO(fromISO) {
  const [y, m, d] = fromISO.split('-').map(Number)
  const dt = new Date(y, m - 1, d + 1)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

// Accepts "16:00", "16:00:00" or "4:00 PM" → "16:00" (null if unparseable)
export function normalizeTime(t) {
  if (!t) return null
  const s = String(t).trim()
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i)
  if (ampm) {
    let h = Number(ampm[1]) % 12
    if (ampm[3].toUpperCase() === 'PM') h += 12
    return `${String(h).padStart(2, '0')}:${ampm[2]}`
  }
  const hm = s.match(/^(\d{1,2}):(\d{2})/)
  return hm ? `${hm[1].padStart(2, '0')}:${hm[2]}` : null
}
